import { Spinner, useDisclosure } from '@chakra-ui/react'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useSelector } from 'react-redux'
import PersonalDetailItem from '../../../../../../components/PersonalDetailItem'
import {
  useGetUserFeeStructureQuery,
  useSuspendAccountMutation,
} from '../../../../../../redux/services/coreApi'
import type { RootState } from '../../../../../../redux/store'
import type { UserDto } from '../../../../../../types/coreApi-types'
import {
  canChangeUserEmail,
  canViewInvestorFeeStructure,
} from '../../../../../../utils/roleUtils'
import AssignFeeStructureModal from './AssignFeeStructureModal'
import ChangeUserEmailModal from './ChangeUserEmailModal'
import UpdateUserDetailsModal from './UpdateUserDetailsModal'

interface UserDetailsProps {
  client?: UserDto
  isLoading: boolean
}

const UserDetails: React.FC<UserDetailsProps> = ({ client, isLoading }) => {
  const isOrganisation = client?.role === 'Organization'
  const isMember = client?.role === 'Member'
  const isIndividual = client?.role === 'Individual'

  const updateUserDetailsModel = useDisclosure()
  const assignFeeStructureModal = useDisclosure()
  const changeUserEmailModal = useDisclosure()

  const [suspendAccount] = useSuspendAccountMutation()

  // Only Admin / FinanceOfficer may view a customer's fee structure. Gate the fetch (not just the display) so
  // scoped staff — e.g. a ComplianceOfficer driving the compliance surface — don't fire an unauthorized
  // request that 403s into an error toast (and Sentry) on an otherwise-normal client view.
  const sessionRole = useSelector((state: RootState) => state.session.role)
  const canViewFeeStructure = canViewInvestorFeeStructure(sessionRole)
  const canChangeEmail = canChangeUserEmail(sessionRole)

  const { data: feeStructure, isLoading: isLoadingFeeStructure } =
    useGetUserFeeStructureQuery(client?.id ?? '', {
      skip: !client?.id || !canViewFeeStructure,
    })

  if (isLoading) {
    return (
      <div className="mt-10 flex justify-center flex-col items-center">
        <Spinner size={'lg'} />
        <p className="text-description mt-4">Please wait ...</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex">
        <h1 className="h1">User details</h1>
        <FontAwesomeIcon
          icon={faEdit}
          className="ml-2 cursor-pointer mt-2"
          onClick={updateUserDetailsModel.onOpen}
        />
      </div>
      <div className="card gap-5">
        <PersonalDetailItem title="Account type" value={client?.role || ''} />
        <PersonalDetailItem title="Name" value={client?.name || ''} />

        <div className="flex items-center gap-2">
          <div className="flex-1">
            <PersonalDetailItem title="Email" value={client?.email || ''} />
          </div>
          {canChangeEmail && client && (
            <FontAwesomeIcon
              icon={faEdit}
              title="Change login email"
              className="cursor-pointer text-description hover:text-brand-accent transition-colors"
              onClick={changeUserEmailModal.onOpen}
            />
          )}
        </div>
        <PersonalDetailItem
          title={isOrganisation ? 'KYB' : 'KYC'}
          value={client?.kycDetails?.kycStatus || ''}
        />
        <PersonalDetailItem title="Phone" value={client?.phone || ''} />
        <PersonalDetailItem
          title="Country of residence"
          value={client?.country || '—'}
        />
        {!isOrganisation && (
          <>
            <PersonalDetailItem
              title="Country of birth"
              value={client?.countryOfBirth || '—'}
            />
            <PersonalDetailItem
              title="Nationality (ID document)"
              value={client?.nationality || '—'}
            />
          </>
        )}
        {canViewFeeStructure && (
          <div className="flex flex-col md:flex-row">
            <p className="text-description mb-0.5 md:mb-0 md:w-[180px]">
              Fee Structure
            </p>
            <div className="flex items-center gap-2">
              <p className="text-default-small">
                {isLoadingFeeStructure
                  ? 'Loading...'
                  : feeStructure?.description || 'Not assigned'}
              </p>
              <FontAwesomeIcon
                icon={faEdit}
                className="cursor-pointer text-description hover:text-brand-accent transition-colors"
                onClick={assignFeeStructureModal.onOpen}
              />
            </div>
          </div>
        )}

        <div className="my-5">
          {client?.isSuspended ? (
            <button
              className="approve-button"
              onClick={async () => {
                await suspendAccount({
                  userId: client.id,
                  isSuspended: false,
                })
              }}
            >
              Un-suspend account
            </button>
          ) : (
            <button
              className="button-danger"
              onClick={async () => {
                await suspendAccount({
                  userId: client!.id,
                  isSuspended: true,
                })
              }}
            >
              Suspend account
            </button>
          )}
        </div>
      </div>
      <UpdateUserDetailsModal
        isOpen={updateUserDetailsModel.isOpen}
        onClose={updateUserDetailsModel.onClose}
        user={client}
      />
      <ChangeUserEmailModal
        isOpen={changeUserEmailModal.isOpen}
        onClose={changeUserEmailModal.onClose}
        user={client}
      />
      {canViewFeeStructure && (
        <AssignFeeStructureModal
          isOpen={assignFeeStructureModal.isOpen}
          onClose={assignFeeStructureModal.onClose}
          user={client}
        />
      )}
    </div>
  )
}

export default UserDetails
